import React, { useEffect, useRef, useState } from 'react'

import GlobalStyles from '../../../components/component-items/styles'
import { StatusBoxStyled } from '../../../components/component-items/status-box'
import { globalState } from '../../../store'
import {
  getUploadMapping,
  getUploadSchema,
  replaceUploadMapping,
  submitOrders,
  uploadLegacyOrders,
  UploadApiError,
  validateOrders,
  validateSource,
} from './api'
import {
  bindingsFromSelection,
  canSubmit,
  inferUploadFormat,
  inspectCsv,
  mergeMappingSelections,
  orderHasIssues,
  orderLabel,
  parsePositiveInteger,
  selectionFromBindings,
  suggestedSelection,
} from './adapters'
import {
  CanonicalItem,
  CanonicalOrder,
  ImportStep,
  SourceColumn,
  SubmissionResult,
  UploadFormat,
  UploadMapping,
  UploadMetadata,
  UploadSchema,
  ValidationResult,
} from './types'
import { MatchStep, OrderEditDrawer, SubmittedStep, UploadStep, ValidationStep, WorkflowProgress } from './components'
import { UploadOrdersStyled } from './styles'

const localId = () =>
  typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `local-${Date.now()}-${Math.random().toString(36).slice(2)}`

const cloneOrder = (order: CanonicalOrder): CanonicalOrder => ({
  ...order,
  fields: { ...order.fields },
  items: order.items.map((item) => ({ ...item, source_rows: [...item.source_rows] })),
  issues: [...order.issues],
  source_rows: [...order.source_rows],
})

const PAGE_SIZE = 100
const REQUIRED_BINDING_FIELDS = new Set(['originator_id', 'item_sku', 'item_quantity'])

export const UploadOrders = () => {
  const [file, setFileState] = useState<File | null>(null)
  const [columns, setColumns] = useState<SourceColumn[]>([])
  const [dataRowCount, setDataRowCount] = useState(0)
  const [inferredFormat, setInferredFormat] = useState<UploadFormat | null>(null)
  const [format, setFormat] = useState<UploadFormat | null>(null)
  const [schema, setSchema] = useState<UploadSchema | null>(null)
  const [savedMapping, setSavedMapping] = useState<UploadMapping | null>(null)
  const [selection, setSelection] = useState<Record<string, number[]>>({})
  const [transformHot, setTransformHot] = useState(false)
  const [mappingSource, setMappingSource] = useState<'saved' | 'one_time'>('one_time')
  const [mappingNotice, setMappingNotice] = useState<string | null>(null)
  const [result, setResult] = useState<ValidationResult | null>(null)
  const [submission, setSubmission] = useState<SubmissionResult | null>(null)
  const [standardSubmitted, setStandardSubmitted] = useState(false)
  const [step, setStep] = useState<ImportStep>('upload')
  const [dirty, setDirty] = useState(false)
  const [dirtyOrderIds, setDirtyOrderIds] = useState<string[]>([])
  const [busy, setBusy] = useState(false)
  const [fullscreen, setFullscreen] = useState(false)
  const [view, setView] = useState<'issues' | 'all'>('issues')
  const [page, setPage] = useState(1)
  const [message, setMessage] = useState<{ type: 'error' | 'success' | 'warning'; text: string } | null>(null)
  const [editingCell, setEditingCell] = useState<{ orderId: string; field: string } | null>(null)
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null)
  const [draftOrder, setDraftOrder] = useState<CanonicalOrder | null>(null)
  const [draftItemQuantities, setDraftItemQuantities] = useState<Record<string, string>>({})
  const busyRef = useRef(false)
  const drawerRef = useRef<HTMLDivElement>(null)
  const returnFocusRef = useRef<HTMLElement | null>(null)
  const {
    state: { csrf, warehouseActive },
  } = globalState()

  const mappingFields = schema?.fields || []
  const reviewFields = mappingFields.filter((field) => !['item_sku', 'item_quantity'].includes(field.key))
  const sections = Array.from(new Set(mappingFields.map((field) => field.section)))
  const sourceOptions = columns.map((column) => ({
    value: column.index,
    label: `${column.header} (column ${column.index + 1})${
      column.samples.length ? ` - ${column.samples.join(', ')}` : ''
    }`,
  }))
  const selectedIndices = new Set(Object.values(selection).flat())
  const skippedColumns = columns.filter((column) => !selectedIndices.has(column.index))
  const missingRequiredMappings = mappingFields.filter(
    (field) => REQUIRED_BINDING_FIELDS.has(field.key) && !selection[field.key]?.length
  )
  const allOrders = result?.orders || []
  const issueOrders = allOrders.filter(orderHasIssues)
  const fileIssues = result?.issues.filter((issue) => !issue.order_id) || []
  const submissionReady = canSubmit(result, dirty)
  const filteredOrders = view === 'issues' ? issueOrders : allOrders
  const totalPages = Math.max(1, Math.ceil(filteredOrders.length / PAGE_SIZE))
  const visibleOrders = filteredOrders.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)
  const selectedOrder = draftOrder
  const persistedSelectedOrder = allOrders.find((order) => order.id === selectedOrderId) || null
  const hasDraftQuantityChanges = Boolean(
    selectedOrder?.items.some((item) => draftItemQuantities[item.id] !== String(item.quantity))
  )
  const hasInvalidDraftQuantity = Boolean(
    selectedOrder?.items.some(
      (item) => parsePositiveInteger(draftItemQuantities[item.id] ?? String(item.quantity)) === null
    )
  )
  const hasDraftChanges = Boolean(
    selectedOrder &&
    persistedSelectedOrder &&
    (JSON.stringify(selectedOrder) !== JSON.stringify(persistedSelectedOrder) || hasDraftQuantityChanges)
  )

  const handleApiError = (error: unknown) => {
    if (error instanceof UploadApiError && error.body.result) {
      setResult(error.body.result)
      setStep('validate')
      setView('issues')
      setPage(1)
      setDirty(false)
      setDirtyOrderIds([])
    }
    setMessage({ type: 'error', text: error instanceof Error ? error.message : 'The request could not be completed.' })
  }

  const run = async (action: () => Promise<void>) => {
    if (busyRef.current) return
    busyRef.current = true
    setBusy(true)
    setMessage(null)
    try {
      await action()
    } catch (error) {
      handleApiError(error)
    } finally {
      busyRef.current = false
      setBusy(false)
    }
  }

  const resetConfigurableState = () => {
    setSchema(null)
    setSavedMapping(null)
    setSelection({})
    setTransformHot(false)
    setMappingSource('one_time')
    setMappingNotice(null)
    setResult(null)
    setSubmission(null)
    setDirty(false)
    setDirtyOrderIds([])
    setView('issues')
    setPage(1)
    setSelectedOrderId(null)
    setDraftOrder(null)
    setDraftItemQuantities({})
  }

  const invalidateValidation = () => {
    setResult(null)
    setMessage(null)
    setDirty(false)
    setDirtyOrderIds([])
    setEditingCell(null)
    setSelectedOrderId(null)
    setDraftOrder(null)
    setDraftItemQuantities({})
  }

  const loadConfigurable = async (sourceColumns: SourceColumn[], rowCount: number) => {
    const [{ data: nextSchema }, { data: mapping }] = await Promise.all([getUploadSchema(csrf), getUploadMapping(csrf)])
    if (nextSchema.row_limit && rowCount > nextSchema.row_limit) {
      throw new Error(`Configurable uploads may contain at most ${nextSchema.row_limit.toLocaleString()} data rows.`)
    }
    const compatibleMapping = mapping?.adapter === 'order.configurable_item_row' ? mapping : null
    const saved = compatibleMapping
      ? selectionFromBindings(compatibleMapping.configuration.bindings, sourceColumns)
      : {}
    const suggestions = suggestedSelection(nextSchema.fields, sourceColumns)
    setSchema(nextSchema)
    setSavedMapping(compatibleMapping)
    setSelection(mergeMappingSelections(suggestions, saved))
    setTransformHot(
      compatibleMapping?.configuration.transforms.hot === 'populated_to_true' && Boolean(saved.hot?.length)
    )
    setMappingSource(Object.keys(saved).length ? 'saved' : 'one_time')
  }

  const setFile = (nextFile: File | null) => {
    if (!nextFile) {
      setFileState(null)
      setColumns([])
      setDataRowCount(0)
      setInferredFormat(null)
      setFormat(null)
      setStep('upload')
      setStandardSubmitted(false)
      resetConfigurableState()
      setMessage(null)
      return
    }
    run(async () => {
      const inspection = await inspectCsv(nextFile)
      const inferred = inferUploadFormat(inspection.columns)
      setFileState(nextFile)
      setColumns(inspection.columns)
      setDataRowCount(inspection.dataRowCount)
      setInferredFormat(inferred)
      setFormat(inferred)
      setStep('upload')
      setStandardSubmitted(false)
      resetConfigurableState()
      if (inferred === 'configurable') await loadConfigurable(inspection.columns, inspection.dataRowCount)
    })
  }

  const changeFormat = (nextFormat: UploadFormat) => {
    if (nextFormat === format) return
    run(async () => {
      setFormat(nextFormat)
      resetConfigurableState()
      if (nextFormat === 'configurable') await loadConfigurable(columns, dataRowCount)
    })
  }

  useEffect(() => {
    if (!fullscreen) return
    const previousOverflow = document.body.style.overflow
    const exitOnEscape = (event: KeyboardEvent) => event.key === 'Escape' && setFullscreen(false)
    document.body.style.overflow = 'hidden'
    window.addEventListener('keydown', exitOnEscape)
    return () => {
      document.body.style.overflow = previousOverflow
      window.removeEventListener('keydown', exitOnEscape)
    }
  }, [fullscreen])

  useEffect(() => {
    if (!file || submission || standardSubmitted) return
    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault()
      event.returnValue = ''
    }
    window.addEventListener('beforeunload', warn)
    return () => window.removeEventListener('beforeunload', warn)
  }, [file, submission, standardSubmitted])

  useEffect(() => {
    if (!selectedOrderId) return
    drawerRef.current?.focus()
    return () => returnFocusRef.current?.focus()
  }, [selectedOrderId])

  useEffect(() => {
    if (!selectedOrderId) return
    const handleDrawerKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        if (hasDraftChanges && !window.confirm('Do you want to discard your changes?')) return
        setSelectedOrderId(null)
        setDraftOrder(null)
        setDraftItemQuantities({})
        return
      }
      if (event.key !== 'Tab' || !drawerRef.current) return
      const focusable = Array.from(
        drawerRef.current.querySelectorAll<HTMLElement>(
          'button:not([disabled]), input:not([disabled]), select:not([disabled])'
        )
      )
      if (!focusable.length) return
      const first = focusable[0]
      const last = focusable[focusable.length - 1]
      if (event.shiftKey && (document.activeElement === first || document.activeElement === drawerRef.current)) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && (document.activeElement === last || document.activeElement === drawerRef.current)) {
        event.preventDefault()
        first.focus()
      }
    }
    window.addEventListener('keydown', handleDrawerKeyDown)
    return () => window.removeEventListener('keydown', handleDrawerKeyDown)
  }, [hasDraftChanges, selectedOrderId])

  const uploadMetadata = (): UploadMetadata => ({
    adapter: format === 'standard' ? 'order.standard_wide' : 'order.configurable_item_row',
    format_overridden: Boolean(inferredFormat && format !== inferredFormat),
    mapping_source: mappingSource,
  })

  const uploadLegacy = () =>
    run(async () => {
      if (!file) return
      const { data } = await uploadLegacyOrders(file, csrf)
      if (!data.success) throw new Error(data.error || 'The Standard Kase template could not be imported.')
      setStandardSubmitted(true)
      setStep('submitted')
    })

  const saveMapping = () =>
    run(async () => {
      if (!schema) return
      const bindings = bindingsFromSelection(schema.fields, columns, selection)
      const missing = schema.fields.filter(
        (field) => REQUIRED_BINDING_FIELDS.has(field.key) && !bindings[field.key]?.length
      )
      if (missing.length) {
        setMappingNotice(`Missing required fields: ${missing.map((field) => field.label).join(', ')}.`)
        return
      }
      const transforms: Record<string, string> = transformHot && bindings.hot ? { hot: 'populated_to_true' } : {}
      const { data } = await replaceUploadMapping(bindings, transforms, csrf)
      setSavedMapping(data)
      setMappingSource('saved')
      setMappingNotice(null)
      setMessage({ type: 'success', text: 'Company preset saved for future order uploads.' })
    })

  const validateFile = () =>
    run(async () => {
      if (!file || !schema) return
      const bindings = bindingsFromSelection(schema.fields, columns, selection)
      const missing = schema.fields.filter(
        (field) => REQUIRED_BINDING_FIELDS.has(field.key) && !bindings[field.key]?.length
      )
      if (missing.length) throw new Error(`Map required fields: ${missing.map((field) => field.label).join(', ')}.`)
      const transforms: Record<string, string> = transformHot && bindings.hot ? { hot: 'populated_to_true' } : {}
      const { data } = await validateSource(file, bindings, transforms, uploadMetadata(), warehouseActive?.id, csrf)
      setResult(data)
      setStep('validate')
      setView('issues')
      setPage(1)
      setDirty(false)
      setDirtyOrderIds([])
    })

  const replaceOrders = (orderId: string, update: (orders: CanonicalOrder[]) => CanonicalOrder[]) => {
    setResult((current) => (current ? { ...current, orders: update(current.orders) } : current))
    setDirty(true)
    setDirtyOrderIds((current) => (current.includes(orderId) ? current : [...current, orderId]))
  }

  const updateOrderField = (orderId: string, field: string, value: string | boolean) =>
    replaceOrders(orderId, (orders) =>
      orders.map((order) =>
        order.id === orderId
          ? {
              ...order,
              originator_id: field === 'originator_id' ? String(value) : order.originator_id,
              fields: { ...order.fields, [field]: value },
              provided_fields: Array.from(new Set([...(order.provided_fields || []), field])),
            }
          : order
      )
    )

  const updateItem = (orderId: string, itemId: string, fields: Partial<Pick<CanonicalItem, 'sku' | 'quantity'>>) =>
    replaceOrders(orderId, (orders) =>
      orders.map((order) =>
        order.id === orderId
          ? { ...order, items: order.items.map((item) => (item.id === itemId ? { ...item, ...fields } : item)) }
          : order
      )
    )

  const revalidate = () =>
    run(async () => {
      if (!result) return
      const { data } = await validateOrders(
        { upload: result.upload, warehouse_id: warehouseActive?.id, orders: result.orders },
        csrf
      )
      setResult(data)
      setDirty(false)
      setDirtyOrderIds([])
      setView('issues')
      setPage(1)
    })

  const submit = () =>
    run(async () => {
      if (!result) return
      const { data } = await submitOrders(
        { upload: result.upload, warehouse_id: warehouseActive?.id, orders: result.orders },
        csrf
      )
      if ('accepted' in data) {
        setSubmission(data)
        setFullscreen(false)
        setStep('submitted')
      } else {
        setResult(data)
        setDirty(false)
        setDirtyOrderIds([])
        setView('issues')
        setPage(1)
        setMessage({ type: 'error', text: 'Submission found issues that must be corrected.' })
      }
    })

  const closeOrderEditor = () => {
    if (hasDraftChanges && !window.confirm('Do you want to discard your changes?')) return
    setSelectedOrderId(null)
    setDraftOrder(null)
    setDraftItemQuantities({})
  }

  const openOrderEditor = (order: CanonicalOrder) => {
    returnFocusRef.current = document.activeElement as HTMLElement
    setFullscreen(false)
    setSelectedOrderId(order.id)
    setDraftOrder(cloneOrder(order))
    setDraftItemQuantities(Object.fromEntries(order.items.map((item) => [item.id, String(item.quantity)])))
  }

  const updateDraftField = (field: string, value: string | boolean) =>
    setDraftOrder((current) =>
      current
        ? {
            ...current,
            originator_id: field === 'originator_id' ? String(value) : current.originator_id,
            fields: { ...current.fields, [field]: value },
            provided_fields: Array.from(new Set([...(current.provided_fields || []), field])),
          }
        : current
    )

  const saveOrderEdits = () => {
    if (!selectedOrderId || !selectedOrder) return
    const quantities = Object.fromEntries(
      selectedOrder.items.map((item) => [
        item.id,
        parsePositiveInteger(draftItemQuantities[item.id] ?? String(item.quantity)),
      ])
    )
    if (Object.values(quantities).some((quantity) => quantity === null)) {
      setMessage({ type: 'error', text: 'Every item quantity must be a positive whole number.' })
      return
    }
    const savedOrder = {
      ...selectedOrder,
      items: selectedOrder.items.map((item) => ({ ...item, quantity: quantities[item.id] as number })),
    }
    replaceOrders(selectedOrderId, (orders) =>
      orders.map((order) => (order.id === selectedOrderId ? cloneOrder(savedOrder) : order))
    )
    setSelectedOrderId(null)
    setDraftOrder(null)
    setDraftItemQuantities({})
  }

  const toggleExcluded = (order: CanonicalOrder, excluded: boolean, confirmDiscard = false) => {
    if (excluded && confirmDiscard && !window.confirm(`Discard order ${orderLabel(order)} from this import?`)) return
    if (
      !excluded &&
      confirmDiscard &&
      hasDraftChanges &&
      !window.confirm(`Restore order ${orderLabel(order)} and discard unsaved changes?`)
    )
      return
    replaceOrders(order.id, (orders) =>
      orders.map((candidate) => (candidate.id === order.id ? { ...candidate, excluded } : candidate))
    )
    setSelectedOrderId(null)
    setDraftOrder(null)
    setDraftItemQuantities({})
  }

  const deleteItem = (orderId: string, itemId: string) =>
    replaceOrders(orderId, (orders) =>
      orders.map((order) =>
        order.id === orderId ? { ...order, items: order.items.filter((item) => item.id !== itemId) } : order
      )
    )

  const addItem = (orderId: string) =>
    replaceOrders(orderId, (orders) =>
      orders.map((order) =>
        order.id === orderId
          ? { ...order, items: [...order.items, { id: localId(), sku: '', quantity: 1, source_rows: [] }] }
          : order
      )
    )

  const updateDraftItemSku = (itemId: string, sku: string) =>
    setDraftOrder((current) =>
      current
        ? { ...current, items: current.items.map((item) => (item.id === itemId ? { ...item, sku } : item)) }
        : current
    )

  const deleteDraftItem = (itemId: string) =>
    setDraftOrder((current) =>
      current ? { ...current, items: current.items.filter((item) => item.id !== itemId) } : current
    )

  const addDraftItem = () =>
    setDraftOrder((current) =>
      current
        ? { ...current, items: [...current.items, { id: localId(), sku: '', quantity: 1, source_rows: [] }] }
        : current
    )

  const renderBody = () => {
    if (step === 'match') {
      return (
        <MatchStep
          schema={schema}
          savedMapping={savedMapping}
          mappingSource={mappingSource}
          mappingFields={mappingFields}
          sections={sections}
          sourceOptions={sourceOptions}
          selection={selection}
          transformHot={transformHot}
          skippedColumns={skippedColumns}
          requiredBindingFields={REQUIRED_BINDING_FIELDS}
          missingRequiredMappings={missingRequiredMappings}
          mappingNotice={mappingNotice}
          busy={busy}
          onSaveMapping={saveMapping}
          onSelectionChange={(fieldKey, nextSelection) => {
            setSelection((current) => ({ ...current, [fieldKey]: nextSelection }))
            invalidateValidation()
            setMappingSource('one_time')
            setMappingNotice(null)
          }}
          onTransformHotChange={(checked) => {
            setTransformHot(checked)
            invalidateValidation()
            setMappingSource('one_time')
            setMappingNotice(null)
          }}
          onBack={() => setStep('upload')}
          onValidate={() => {
            setMappingNotice(null)
            validateFile()
          }}
          onMissingRequiredMappings={() =>
            setMappingNotice(
              `Missing required fields: ${missingRequiredMappings.map((field) => field.label).join(', ')}.`
            )
          }
        />
      )
    }
    if (step === 'validate' && result) {
      return (
        <ValidationStep
          result={result}
          dirty={dirty}
          dirtyOrderIds={dirtyOrderIds}
          fileIssues={fileIssues}
          fullscreen={fullscreen}
          view={view}
          issueOrderCount={issueOrders.length}
          allOrderCount={allOrders.length}
          reviewFields={reviewFields}
          visibleOrders={visibleOrders}
          editingCell={editingCell}
          busy={busy}
          page={page}
          totalPages={totalPages}
          submissionReady={submissionReady}
          onViewChange={(nextView) => {
            setView(nextView)
            setPage(1)
          }}
          onToggleFullscreen={() => setFullscreen((current) => !current)}
          onEditingCellChange={setEditingCell}
          onUpdateOrderField={updateOrderField}
          onUpdateItem={updateItem}
          onDeleteItem={deleteItem}
          onAddItem={addItem}
          onQuantityError={() => setMessage({ type: 'error', text: 'Quantity must be a positive whole number.' })}
          onToggleExcluded={toggleExcluded}
          onOpenOrderEditor={openOrderEditor}
          onPageChange={setPage}
          onBack={() => {
            setFullscreen(false)
            invalidateValidation()
            setStep('match')
          }}
          onRevalidate={revalidate}
          onSubmit={submit}
        />
      )
    }
    if (step === 'submitted') {
      return <SubmittedStep file={file} submission={submission} onUploadAnotherFile={() => setFile(null)} />
    }
    return (
      <UploadStep
        file={file}
        columns={columns}
        dataRowCount={dataRowCount}
        inferredFormat={inferredFormat}
        format={format}
        busy={busy}
        schemaReady={Boolean(schema)}
        onSetFile={setFile}
        onChangeFormat={changeFormat}
        onContinue={() => (format === 'standard' ? uploadLegacy() : setStep('match'))}
      />
    )
  }

  return (
    <UploadOrdersStyled>
      <GlobalStyles.FullPageCard>
        <GlobalStyles.CardHeader>
          <GlobalStyles.CardTitle>
            <h3>Upload Orders</h3>
          </GlobalStyles.CardTitle>
        </GlobalStyles.CardHeader>
        <div className="content">
          <WorkflowProgress step={step} />
          {message ? (
            <div className="upload-message">
              <StatusBoxStyled
                role={message.type === 'error' ? 'alert' : 'status'}
                aria-live={message.type === 'error' ? 'assertive' : 'polite'}
                className={message.type === 'success' ? 'green' : message.type === 'warning' ? 'yellow' : 'red'}
              >
                {message.text}
              </StatusBoxStyled>
            </div>
          ) : null}
          {renderBody()}
        </div>
      </GlobalStyles.FullPageCard>
      {selectedOrder ? (
        <OrderEditDrawer
          order={selectedOrder}
          sections={sections}
          reviewFields={reviewFields}
          busy={busy}
          drawerRef={drawerRef}
          draftItemQuantities={draftItemQuantities}
          hasDraftChanges={hasDraftChanges}
          hasInvalidDraftQuantity={hasInvalidDraftQuantity}
          onClose={closeOrderEditor}
          onUpdateField={updateDraftField}
          onUpdateItemSku={updateDraftItemSku}
          onUpdateItemQuantity={(itemId, quantity) =>
            setDraftItemQuantities((current) => ({ ...current, [itemId]: quantity }))
          }
          onDeleteItem={deleteDraftItem}
          onAddItem={addDraftItem}
          onToggleExcluded={() => toggleExcluded(selectedOrder, !selectedOrder.excluded, true)}
          onSave={saveOrderEdits}
        />
      ) : null}
    </UploadOrdersStyled>
  )
}
